import { lockScroll, unlockScroll } from '@theme/utilities';

const FADE_DURATION = 180;
const PHOTO_CHANGE_DURATION = 140;

/** A single-photo inspector for the main product gallery. */
class ProductPhotoInspector extends HTMLElement {
  /** @type {HTMLElement | null} */
  #gallery = null;
  /** @type {HTMLButtonElement[]} */
  #triggers = [];
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {HTMLElement | null} */
  #stage = null;
  /** @type {{ mediaIndex: number, template: HTMLTemplateElement }[]} */
  #photos = [];
  /** @type {HTMLElement | null} */
  #trigger = null;
  #activePosition = 0;
  #closing = false;
  /** @type {number | null} */
  #closeTimer = null;
  /** @type {number | null} */
  #photoChangeTimer = null;
  #photoChangeToken = 0;

  connectedCallback() {
    const gallery = this.closest('media-gallery');
    const dialog = this.querySelector('dialog');
    const stage = this.querySelector('[data-product-photo-stage]');
    this.#gallery = gallery instanceof HTMLElement ? gallery : null;
    this.#dialog = dialog instanceof HTMLDialogElement ? dialog : null;
    this.#stage = stage instanceof HTMLElement ? stage : null;
    this.#triggers = Array.from(this.#gallery?.querySelectorAll('button[data-product-photo-index]') ?? [])
      .filter((trigger) => trigger instanceof HTMLButtonElement);
    this.#photos = Array.from(this.querySelectorAll('template[data-product-photo-index]'))
      .filter((template) => template instanceof HTMLTemplateElement)
      .map((template) => ({
        mediaIndex: Number(template.dataset.productPhotoIndex),
        template,
      }))
      .filter(({ mediaIndex, template }) => Number.isInteger(mediaIndex) && template.content.querySelector('img'));

    this.#triggers.forEach((trigger) => trigger.addEventListener('click', this.#handleGalleryClick));
    this.#dialog?.addEventListener('click', this.#handleDialogClick);
    this.#dialog?.addEventListener('keydown', this.#handleKeyDown);
    this.#dialog?.addEventListener('cancel', this.#handleCancel);
  }

  disconnectedCallback() {
    this.#triggers.forEach((trigger) => trigger.removeEventListener('click', this.#handleGalleryClick));
    this.#dialog?.removeEventListener('click', this.#handleDialogClick);
    this.#dialog?.removeEventListener('keydown', this.#handleKeyDown);
    this.#dialog?.removeEventListener('cancel', this.#handleCancel);
    if (this.#closeTimer !== null) window.clearTimeout(this.#closeTimer);
    if (this.#photoChangeTimer !== null) window.clearTimeout(this.#photoChangeTimer);
    this.#photoChangeToken += 1;
    if (this.#dialog?.open) this.#dialog.close();
    if (this.#dialog) unlockScroll(this.#dialog);
  }

  /** @param {MouseEvent} event */
  #handleGalleryClick = (event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLButtonElement)) return;

    const mediaIndex = Number(trigger.dataset.productPhotoIndex);
    // The slideshow prevents the synthetic click after a drag at the document level.
    // Wait until the event finishes bubbling so a swipe cannot open the inspector.
    queueMicrotask(() => {
      if (event.defaultPrevented || !this.isConnected || !trigger.isConnected) return;
      this.open(mediaIndex, trigger);
    });
  };

  /** @param {MouseEvent} event */
  #handleDialogClick = (event) => {
    if (event.target === this.#dialog || event.target === this.#stage) {
      this.close();
      return;
    }

    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('button[data-product-photo-action]');
    if (!(control instanceof HTMLButtonElement) || !this.#dialog?.contains(control)) return;

    switch (control.dataset.productPhotoAction) {
      case 'close':
        this.close();
        break;
      case 'previous':
        this.#showPhoto(this.#activePosition - 1);
        break;
      case 'next':
        this.#showPhoto(this.#activePosition + 1);
        break;
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#showPhoto(this.#activePosition - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#showPhoto(this.#activePosition + 1);
    }
  };

  /** @param {Event} event */
  #handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** @param {number} mediaIndex @param {HTMLElement} trigger */
  open(mediaIndex, trigger) {
    const position = this.#photos.findIndex((photo) => photo.mediaIndex === mediaIndex);
    if (position < 0 || !this.#dialog || !this.#stage || this.#closing) return;

    this.#trigger = trigger;
    this.#showPhoto(position);
    if (this.#dialog.open) return;

    this.#dialog.showModal();
    lockScroll(this.#dialog);
    // Commit the transparent state before starting the fade.
    void this.#dialog.offsetWidth;
    this.#dialog.classList.add('is-visible');
    const closeButton = this.#dialog.querySelector('button[data-product-photo-action="close"]');
    if (closeButton instanceof HTMLButtonElement) closeButton.focus({ preventScroll: true });
  }

  /** @param {number} position */
  #showPhoto(position) {
    if (position < 0 || position >= this.#photos.length || !this.#stage || this.#closing) return;
    if (position === this.#activePosition && this.#stage.querySelector('img')) return;

    const image = this.#photos[position].template.content.querySelector('img');
    if (!image) return;

    const activeImage = /** @type {HTMLImageElement} */ (image.cloneNode(true));
    activeImage.draggable = false;
    this.#activePosition = position;

    const previous = this.#dialog?.querySelector('button[data-product-photo-action="previous"]');
    const next = this.#dialog?.querySelector('button[data-product-photo-action="next"]');
    if (previous instanceof HTMLButtonElement) previous.disabled = position === 0;
    if (next instanceof HTMLButtonElement) next.disabled = position === this.#photos.length - 1;

    const count = this.#dialog?.querySelector('[data-product-photo-count]');
    if (count) count.textContent = `${position + 1} / ${this.#photos.length}`;

    if (this.#photoChangeTimer !== null) window.clearTimeout(this.#photoChangeTimer);
    this.#photoChangeToken += 1;
    const changeToken = this.#photoChangeToken;
    const currentImage = this.#stage.querySelector('img');
    const shouldFade =
      this.#dialog?.open &&
      currentImage &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!shouldFade) {
      this.#stage.classList.remove('is-changing');
      this.#stage.replaceChildren(activeImage);
      return;
    }

    this.#stage.classList.add('is-changing');
    this.#photoChangeTimer = window.setTimeout(() => {
      if (changeToken !== this.#photoChangeToken || !this.#stage) return;

      let hasRevealed = false;
      const revealImage = () => {
        if (hasRevealed) return;
        hasRevealed = true;
        requestAnimationFrame(() => {
          if (changeToken !== this.#photoChangeToken || !this.#stage || this.#closing) return;
          this.#stage.classList.remove('is-changing');
          this.#photoChangeTimer = null;
        });
      };

      activeImage.addEventListener('load', revealImage, { once: true });
      activeImage.addEventListener('error', revealImage, { once: true });
      this.#stage.replaceChildren(activeImage);
      if (activeImage.complete) revealImage();
      this.#photoChangeTimer = null;
    }, PHOTO_CHANGE_DURATION);
  }

  close() {
    if (!this.#dialog?.open || this.#closing) return;
    this.#closing = true;
    if (this.#photoChangeTimer !== null) window.clearTimeout(this.#photoChangeTimer);
    this.#photoChangeTimer = null;
    this.#photoChangeToken += 1;
    this.#dialog.classList.remove('is-visible');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#finishClose();
    } else {
      this.#closeTimer = window.setTimeout(this.#finishClose, FADE_DURATION);
    }
  }

  #finishClose = () => {
    this.#closeTimer = null;
    if (this.#dialog?.open) this.#dialog.close();
    if (this.#dialog) unlockScroll(this.#dialog);
    this.#stage?.classList.remove('is-changing');
    this.#stage?.replaceChildren();
    if (this.#trigger?.isConnected) this.#trigger.focus({ preventScroll: true });
    this.#trigger = null;
    this.#closing = false;
  };
}

if (!customElements.get('product-photo-inspector')) {
  customElements.define('product-photo-inspector', ProductPhotoInspector);
}
